import { UserAccessLevel } from '../../types/user';

/** Minimal principal shape from JWT / Redux session. */
export interface OrgAccessPrincipal {
  AccessLevel?: string | number | null;
  OrganizationID?: string | number | null;
  OrganizationIDs?: Array<string | number> | null;
  MaskFaultDetails?: boolean;
  MaskFaultDetailsByOrganization?: Record<string, boolean> | null;
}

export function parseAccessLevel(user: OrgAccessPrincipal | null | undefined): number {
  if (user?.AccessLevel == null || user.AccessLevel === '') return Number.NaN;
  return parseInt(String(user.AccessLevel), 10);
}

export function isSuperAdmin(user: OrgAccessPrincipal | null | undefined): boolean {
  return parseAccessLevel(user) === UserAccessLevel.SUPER_ADMIN;
}

export function isOrgAdmin(user: OrgAccessPrincipal | null | undefined): boolean {
  return parseAccessLevel(user) === UserAccessLevel.ORGANIZATION_ADMIN;
}

export function isOrganizationViewer(user: OrgAccessPrincipal | null | undefined): boolean {
  return parseAccessLevel(user) === UserAccessLevel.ORGANIZATION_VIEWER;
}

export function isRegularUser(user: OrgAccessPrincipal | null | undefined): boolean {
  return parseAccessLevel(user) === UserAccessLevel.REGULAR_USER;
}

/** Regular User or Organization Viewer — hide writes, keep operational reads. */
export function isReadOnlyPrincipal(user: OrgAccessPrincipal | null | undefined): boolean {
  return isRegularUser(user) || isOrganizationViewer(user);
}

function parsePositiveOrgIds(ids: Array<string | number> | null | undefined): number[] {
  return Array.from(
    new Set(
      (ids || [])
        .map(id => parseInt(String(id), 10))
        .filter(id => Number.isFinite(id) && id > 0)
    )
  );
}

/** Viewer grant list from JWT `OrganizationIDs`. Empty for non-viewers. */
export function getGrantedOrganizationIds(
  user: OrgAccessPrincipal | null | undefined
): number[] {
  if (!isOrganizationViewer(user)) {
    return [];
  }
  return parsePositiveOrgIds(user?.OrganizationIDs);
}

/** Home / legacy scalar org id as a string, or null. */
export function getHomeOrganizationId(
  user: OrgAccessPrincipal | null | undefined
): string | null {
  if (user?.OrganizationID == null || user.OrganizationID === '') return null;
  return String(user.OrganizationID);
}

/**
 * Authoritative ACL for org admins from JWT `OrganizationIDs`.
 * Falls back to home `OrganizationID` when the array is missing/empty
 * (feature flag off or legacy tokens).
 * Non–org-admins never use `OrganizationIDs` as an ACL (claim is empty by contract).
 */
export function getManagedOrganizationIds(
  user: OrgAccessPrincipal | null | undefined
): number[] {
  if (!isOrgAdmin(user)) {
    return [];
  }

  const fromAcl = parsePositiveOrgIds(user?.OrganizationIDs);

  if (fromAcl.length > 0) {
    return Array.from(new Set(fromAcl));
  }

  const home = getHomeOrganizationId(user);
  if (home) {
    const homeId = parseInt(home, 10);
    return Number.isFinite(homeId) && homeId > 0 ? [homeId] : [];
  }

  return [];
}

/** True when an org admin has more than one assigned organization. */
export function isMultiOrgAdmin(user: OrgAccessPrincipal | null | undefined): boolean {
  return isOrgAdmin(user) && getManagedOrganizationIds(user).length > 1;
}

/**
 * Default org for forms when a single default is needed:
 * home if present (and in ACL when ACL exists), else first ACL entry.
 */
export function getDefaultOrganizationId(
  user: OrgAccessPrincipal | null | undefined
): string | null {
  const managed = getManagedOrganizationIds(user);
  const home = getHomeOrganizationId(user);

  if (home) {
    const homeNum = parseInt(home, 10);
    if (managed.length === 0 || managed.includes(homeNum)) {
      return home;
    }
  }

  if (managed.length > 0) {
    return String(managed[0]);
  }

  return home;
}

/** Super admins, or org admins who can choose among assigned orgs. */
export function canSelectOrganization(user: OrgAccessPrincipal | null | undefined): boolean {
  if (isSuperAdmin(user)) return true;
  if (isOrgAdmin(user)) return getManagedOrganizationIds(user).length > 0;
  return false;
}

/**
 * Show org filter UI: super admins always; org admins or viewers when
 * they have more than one assigned organization.
 */
export function canFilterByOrganization(user: OrgAccessPrincipal | null | undefined): boolean {
  if (isSuperAdmin(user)) return true;
  if (isMultiOrgAdmin(user)) return true;
  return isOrganizationViewer(user) && getGrantedOrganizationIds(user).length > 1;
}

/** Group filter UI: member admins (1–3) and Organization Viewers. */
export function canFilterByGroup(user: OrgAccessPrincipal | null | undefined): boolean {
  const level = parseAccessLevel(user);
  return (
    level === UserAccessLevel.SUPER_ADMIN ||
    level === UserAccessLevel.ORGANIZATION_ADMIN ||
    level === UserAccessLevel.DEVICE_GROUP_ADMIN ||
    level === UserAccessLevel.ORGANIZATION_VIEWER
  );
}

/** Unassigned shortcut, page, filter, and count: super admins, org admins, and viewers. */
export function canSeeUnassignedGroupFilter(
  user: OrgAccessPrincipal | null | undefined
): boolean {
  return isSuperAdmin(user) || isOrgAdmin(user) || isOrganizationViewer(user);
}

/** Viewer with no OrganizationIDs grants — show empty state, do not query unscoped. */
export function hasEmptyViewerGrants(user: OrgAccessPrincipal | null | undefined): boolean {
  return isOrganizationViewer(user) && getGrantedOrganizationIds(user).length === 0;
}

/** Whether the actor may assign multiple orgs to an organization admin. */
export function canMultiAssignOrganizations(
  user: OrgAccessPrincipal | null | undefined
): boolean {
  return isSuperAdmin(user) || isMultiOrgAdmin(user);
}

/**
 * Whether every org id is assignable by the actor.
 * Super admins may assign any org; org admins are limited to their managed ACL.
 */
export function areOrganizationIdsAssignable(
  actor: OrgAccessPrincipal | null | undefined,
  organizationIds: Array<string | number>
): boolean {
  if (isSuperAdmin(actor)) return true;

  const managed = new Set(getManagedOrganizationIds(actor));
  if (managed.size === 0) return false;

  return organizationIds.every(id => {
    const parsed = parseInt(String(id), 10);
    return Number.isFinite(parsed) && managed.has(parsed);
  });
}

/**
 * Prefer per-device / per-org masking when present so multi-grant viewers
 * are not stuck with a single JWT scalar from the first granted org.
 */
export function shouldMaskFaultDetails(
  user: OrgAccessPrincipal & { MaskFaultDetails?: boolean } | null | undefined,
  device?: {
    MaskFaultDetails?: boolean;
    OrganizationID?: string | number | null;
  } | null
): boolean {
  if (typeof device?.MaskFaultDetails === 'boolean') {
    return device.MaskFaultDetails;
  }
  const orgId = device?.OrganizationID != null ? String(device.OrganizationID) : '';
  const byOrg = user?.MaskFaultDetailsByOrganization;
  if (orgId && byOrg && Object.prototype.hasOwnProperty.call(byOrg, orgId)) {
    return Boolean(byOrg[orgId]);
  }
  return Boolean(user?.MaskFaultDetails);
}
